import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Star } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { Textarea } from '../components/Textarea.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface CsatLoad {
  ticket: {
    displayNumber: number;
    subject: string;
    organizationName: string;
  } | null;
  alreadySubmitted: boolean;
  score: number | null;
  comment: string | null;
}

export function CsatPage(): React.ReactElement {
  useDocumentTitle('Rate your ticket');
  const { token = '' } = useParams<{ token: string }>();
  const [data, setData] = useState<CsatLoad | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    apiFetch<CsatLoad>(`/api/csat/${encodeURIComponent(token)}`)
      .then((r) => {
        setData(r);
        if (r.alreadySubmitted) {
          setScore(r.score);
          setComment(r.comment ?? '');
          setDone(true);
        }
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Survey not found.'))
      .finally(() => setLoading(false));
  }, [token]);

  async function submit(): Promise<void> {
    if (!score) {
      setError('Pick a star rating first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/csat/${encodeURIComponent(token)}`, {
        method: 'POST',
        json: { score, comment: comment.trim() || null },
      });
      setDone(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to submit.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-lg p-8">
        <p className="text-[var(--color-muted)]">Loading…</p>
      </div>
    );
  }
  if (!data || !data.ticket) {
    return (
      <div className="mx-auto max-w-lg p-8">
        <Card>
          <h1 className="text-xl font-bold text-[var(--color-header)]">Survey not found</h1>
          <p className="mt-2 text-sm text-[var(--color-muted)]">
            This rating link is no longer valid. If you have feedback, reply directly to the ticket
            email and we'll see it.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg space-y-4 p-8">
      <Card>
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--color-t3-blue)]">
          T-{data.ticket.displayNumber} · {data.ticket.organizationName}
        </div>
        <h1 className="mt-1 font-display text-2xl font-bold text-[var(--color-header)]">
          {done ? 'Thanks for the feedback' : 'How did we do?'}
        </h1>
        <p className="mt-2 text-sm text-[var(--color-muted)]">
          {done ? 'Your rating has been recorded.' : data.ticket.subject}
        </p>

        <div className="mt-5">
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                disabled={done || busy}
                onClick={() => setScore(n)}
                className={
                  'rounded-full p-2 transition ' +
                  (score !== null && n <= score
                    ? 'text-amber-500'
                    : 'text-[var(--color-muted)] hover:text-amber-500')
                }
                aria-label={`${n} star${n === 1 ? '' : 's'}`}
              >
                <Star
                  className="h-10 w-10"
                  fill={score !== null && n <= score ? 'currentColor' : 'none'}
                  strokeWidth={1.5}
                />
              </button>
            ))}
          </div>
          <div className="mt-2 text-center text-xs text-[var(--color-muted)]">
            {score === null ? 'Tap a star' : score === 5 ? 'Excellent' : score === 4 ? 'Good' : score === 3 ? 'OK' : score === 2 ? 'Below average' : 'Poor'}
          </div>
        </div>

        {!done && (
          <div className="mt-5">
            <Textarea
              label="Comment (optional)"
              name="comment"
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={2000}
              placeholder="Anything you want us to know?"
            />
          </div>
        )}

        {done && comment && (
          <div className="mt-5">
            <div className="text-xs uppercase tracking-wide text-[var(--color-muted)]">
              Your comment
            </div>
            <p className="mt-1 whitespace-pre-wrap text-sm">{comment}</p>
          </div>
        )}

        {error && <p className="mt-4 text-sm text-[var(--color-danger)]">{error}</p>}

        {!done && (
          <div className="mt-5">
            <Button onClick={() => void submit()} disabled={busy || score === null}>
              {busy ? 'Submitting…' : 'Submit rating'}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
